<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Deployments</h1>

    <!-- Awaiting Deployment Signups -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Signups Awaiting Deployment</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <select
          v-model="filters.assignedTo"
          aria-label="Filter tasks by assigned user"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">All Users</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
        </select>

        <select
          v-model="filters.status"
          aria-label="Filter tasks by status"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="any">Any</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>

        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
      </div>

      <AlertBanner
        v-if="error && awaitingSignups.length"
        type="error"
        message="Couldn't refresh awaiting signups — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading awaiting signups...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !awaitingSignups.length"
        title="Couldn't load awaiting signups"
        :message="error"
        @retry="fetchAwaitingSignups"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No signups awaiting deployment</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate="selected.length > 0 && selected.length < sortedAndFiltered.length"
                  @change="toggleAll"
                  aria-label="Select all signups"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </th>
              <th :aria-sort="ariaSort('fullName')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('fullName')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Name
                  <span v-if="sortState.key === 'fullName'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('phone')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('phone')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Phone
                  <span v-if="sortState.key === 'phone'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('community')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('community')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Community
                  <span v-if="sortState.key === 'community'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('villaNumber')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('villaNumber')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Villa
                  <span v-if="sortState.key === 'villaNumber'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('createdAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('createdAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Created
                  <span v-if="sortState.key === 'createdAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="signup in sortedAndFiltered" :key="signup.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :value="signup.id"
                  v-model="selected"
                  :aria-label="`Select signup ${signup.fullName || signup.id}`"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ signup.fullName }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ signup.phone }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ signup.community }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ signup.villaNumber }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (signup.createdAt || '').slice(0,10) }}</td>
              <td class="px-4 py-3 text-sm">
                <button
                  @click="assignSingle(signup)"
                  :disabled="assigningId === signup.id || bulkAssigning"
                  class="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="assigningId === signup.id" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Assign
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <button
          @click="assignBulk"
          :disabled="!selected.length || !assignmentUser || bulkAssigning"
          class="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="bulkAssigning" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ bulkAssigning ? 'Assigning…' : `Assign Selected to ${assignmentUser ? (users.find(u => u.id === assignmentUser)?.username || 'User') : 'User'}` }}
        </button>

        <select
          v-model="assignmentUser"
          aria-label="Assign to user"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">Select User</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
        </select>

        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Total: {{ awaitingSignups.length }}
        </span>
      </div>
    </div>

    <!-- Deployment Tasks -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Deployment Tasks</h2>

      <AlertBanner
        v-if="!tasksLoading && !tasksError && limitReached"
        type="info"
        :message="`Showing the first ${deploymentTasks.length} tasks — narrow your filters to see the rest.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="tasksLoading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading deployment tasks...</p>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="tasksError"
        title="Couldn't load deployment tasks"
        :message="tasksError"
        @retry="loadTasks"
      />

      <!-- Empty State -->
      <div v-else-if="!deploymentTasks.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No deployment tasks found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Type</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Villa - Community</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Container Serial</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Assigned To</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Status</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Date</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="task in deploymentTasks" :key="task.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': task.type === 'deployment',
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': task.type === 'swap',
                    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300': task.type === 'deployment_task',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !task.type
                  }"
                >
                  {{ humanize(task.type) || 'Task' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                <template v-if="task.householdVilla">
                  {{ task.householdVilla }}<span v-if="task.householdCommunity" class="text-neutral-500 dark:text-neutral-400"> - {{ task.householdCommunity }}</span>
                </template>
                <template v-else>
                  {{ task.householdId }}
                </template>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ task.containerSerial || task.installedContainerId || '-' }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ users.find(u => u.id === task.assignedTo || u.id === task.performedBy)?.username || task.assignedTo || task.performedBy || '-' }}</td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': task.status === 'assigned',
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': task.status === 'in_progress',
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': task.status === 'completed',
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': task.status === 'cancelled',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !['assigned', 'in_progress', 'completed', 'cancelled'].includes(task.status)
                  }"
                >
                  {{ humanize(task.status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (task.createdAt || '').slice(0,10) }}</td>
              <td class="px-4 py-3 text-sm">
                <div class="flex items-center gap-2">
                  <select
                    @change="onStatusChange(task, $event)"
                    :value="task.status"
                    :disabled="updatingTaskId === task.id"
                    aria-label="Change task status"
                    class="text-sm px-3 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>

                  <select
                    @change="onReassign(task, $event)"
                    :value="task.assignedTo"
                    :disabled="updatingTaskId === task.id"
                    aria-label="Reassign task"
                    class="text-sm px-3 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
                  </select>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ConfirmDialog
      :show="!!pendingCancel"
      title="Cancel deployment task?"
      :message="`Cancel the deployment task for ${pendingCancel?.task?.householdVilla || pendingCancel?.task?.householdId || 'this household'}? The assigned field worker will no longer see it.`"
      confirm-label="Cancel Task"
      cancel-label="Keep Task"
      :loading="cancelTaskLoading"
      danger
      @confirm="confirmCancelTask"
      @cancel="dismissCancelTask"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseDeployments from '@/composables/useHomebaseDeployments'
import useToast from '@/composables/useToast'
import client from '@/composables/homebaseClient'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'

export default {
  name: 'HomeBaseDeployments',
  components: { ErrorState, AlertBanner, ConfirmDialog },
  setup() {
    const {
      awaitingSignups,
      deploymentTasks,
      users,
      loading,
      error,
      params,
      fetchAwaitingSignups,
      fetchUsers,
      assignDeployment,
      reassignDeployment,
      updateTaskStatus
    } = useHomebaseDeployments()

    const { showToast } = useToast()

    const filters = reactive({
      assignedTo: '',
      status: 'assigned'
    })

    const selected = ref([])
    const assignmentUser = ref('')

    // The composable's fetchDeploymentTasks swallows errors and never toggles
    // a loading flag, so the view fetches tasks itself (same endpoint/params)
    // to drive a real loading/error state for this section.
    const tasksLoading = ref(false)
    const tasksError = ref(null)
    const loadTasks = async () => {
      tasksLoading.value = true
      tasksError.value = null
      try {
        const q = new URLSearchParams()
        if (params.value.assignedTo) q.set('assignedTo', params.value.assignedTo)
        if (params.value.status) q.set('status', params.value.status)
        if (params.value.type) q.set('type', params.value.type)
        if (params.value.limit) q.set('limit', String(params.value.limit))
        if (params.value.sortBy) q.set('sortBy', params.value.sortBy)
        if (params.value.sortDir) q.set('sortDir', params.value.sortDir)
        const res = await client.get(`/deployments${q.toString() ? `?${q.toString()}` : ''}`)
        deploymentTasks.value = Array.isArray(res.data) ? res.data : []
      } catch (err) {
        tasksError.value = err.detail || err.message || 'Failed to load deployment tasks'
      } finally {
        tasksLoading.value = false
      }
    }

    const limitReached = computed(() => deploymentTasks.value.length >= (params.value.limit || 100))

    const applyFilters = () => {
      params.value.assignedTo = filters.assignedTo
      params.value.status = filters.status === 'any' ? '' : filters.status
      loadTasks()
    }

    const reload = () => {
      applyFilters()
      fetchAwaitingSignups()
    }

    const sortState = reactive({ key: 'createdAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return [...awaitingSignups.value].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    // 'in_progress' -> 'In Progress', 'deployment_task' -> 'Deployment Task'
    const humanize = (value) => {
      if (!value) return value
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    }

    const allSelected = computed(() => selected.value.length > 0 && selected.value.length === sortedAndFiltered.value.length)
    const toggleAll = (e) => {
      if (e.target.checked) selected.value = sortedAndFiltered.value.map(s => s.id)
      else selected.value = []
    }

    const assigningId = ref(null)
    const bulkAssigning = ref(false)

    const assignSingle = async (signup) => {
      if (!assignmentUser.value) {
        showToast('info', 'Select a user from the dropdown first')
        return
      }
      if (assigningId.value) return

      // The household is created during batch approval — without it the
      // signup can't be assigned yet.
      const householdId = signup.linkedHouseholdId || signup.householdId
      if (!householdId) {
        showToast('error', "This signup hasn't been approved yet — use Request Deployment on the Signups page first.")
        return
      }

      assigningId.value = signup.id
      try {
        await assignDeployment(householdId, assignmentUser.value)
        await fetchAwaitingSignups()
        showToast('success', 'Deployment assigned')
      } catch (err) {
        showToast('error', 'Failed to assign deployment. Please try again.')
      } finally {
        assigningId.value = null
      }
    }

    const assignBulk = async () => {
      if (!selected.value.length || !assignmentUser.value || bulkAssigning.value) return

      // Validate that all selected signups have household IDs
      const invalidSignups = []
      const validSignups = []

      for (const signupId of selected.value) {
        const signup = awaitingSignups.value.find(s => s.id === signupId)
        const householdId = signup.linkedHouseholdId || signup.householdId

        if (!householdId) {
          invalidSignups.push(signup)
        } else {
          validSignups.push({ signup, householdId })
        }
      }

      if (invalidSignups.length > 0) {
        showToast('error', `${invalidSignups.length} selected signup(s) haven't been approved yet — use Request Deployment on the Signups page first.`)
        return
      }

      bulkAssigning.value = true
      try {
        const promises = validSignups.map(({ householdId }) => {
          return assignDeployment(householdId, assignmentUser.value)
        })
        await Promise.all(promises)
        const count = promises.length
        selected.value = []
        await fetchAwaitingSignups()
        showToast('success', `${count} deployment${count === 1 ? '' : 's'} assigned`)
      } catch (err) {
        showToast('error', 'Failed to assign the selected deployments. Please try again.')
      } finally {
        bulkAssigning.value = false
      }
    }

    const updatingTaskId = ref(null)
    const pendingCancel = ref(null) // { task, el } — el reverts the select on dismiss/failure
    const cancelTaskLoading = ref(false)

    const applyTaskStatus = async (task, status, el) => {
      updatingTaskId.value = task.id
      try {
        // updateTaskStatus refreshes the composable's task list on success.
        await updateTaskStatus(task.id, status)
        showToast('success', 'Task status updated')
        return true
      } catch (err) {
        if (el) el.value = task.status
        showToast('error', 'Failed to update task status. Please try again.')
        return false
      } finally {
        updatingTaskId.value = null
      }
    }

    const onStatusChange = (task, event) => {
      const status = event.target.value
      if (status === task.status) return
      if (status === 'cancelled') {
        // Cancelling is destructive — confirm before the PATCH fires.
        pendingCancel.value = { task, el: event.target }
        return
      }
      applyTaskStatus(task, status, event.target)
    }

    const confirmCancelTask = async () => {
      if (!pendingCancel.value || cancelTaskLoading.value) return
      const { task, el } = pendingCancel.value
      cancelTaskLoading.value = true
      const ok = await applyTaskStatus(task, 'cancelled', el)
      cancelTaskLoading.value = false
      if (ok) pendingCancel.value = null
    }

    const dismissCancelTask = () => {
      if (cancelTaskLoading.value) return
      const { task, el } = pendingCancel.value || {}
      if (el) el.value = task.status
      pendingCancel.value = null
    }

    const onReassign = async (task, event) => {
      const assignedTo = event.target.value
      if (!assignedTo || assignedTo === task.assignedTo) return
      updatingTaskId.value = task.id
      try {
        // reassignDeployment refreshes the composable's task list on success.
        await reassignDeployment(task.id, assignedTo)
        showToast('success', 'Task reassigned')
      } catch (err) {
        event.target.value = task.assignedTo
        showToast('error', 'Failed to reassign task. Please try again.')
      } finally {
        updatingTaskId.value = null
      }
    }

    onMounted(() => {
      fetchAwaitingSignups()
      loadTasks()
      fetchUsers()
    })

    return {
      awaitingSignups,
      deploymentTasks,
      users,
      loading,
      error,
      filters,
      selected,
      assignmentUser,
      fetchAwaitingSignups,
      tasksLoading,
      tasksError,
      loadTasks,
      limitReached,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      humanize,
      allSelected,
      toggleAll,
      assigningId,
      bulkAssigning,
      assignSingle,
      assignBulk,
      updatingTaskId,
      pendingCancel,
      cancelTaskLoading,
      onStatusChange,
      confirmCancelTask,
      dismissCancelTask,
      onReassign
    }
  }
}
</script>
